import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { recordApiSample } from './observability';
import { expireWebSession } from './session-expiry';

type TimedAxiosConfig = InternalAxiosRequestConfig & {
  __startedAt?: number;
};

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL ?? 'http://localhost:3000'}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  (config as TimedAxiosConfig).__startedAt = performance.now();
  return config;
});

api.interceptors.response.use(
  (response) => {
    const startedAt = (response.config as TimedAxiosConfig).__startedAt;
    const durationMs = startedAt ? Math.max(0, performance.now() - startedAt) : 0;
    recordApiSample({
      method: String(response.config.method || 'GET').toUpperCase(),
      path: String(response.config.url || ''),
      durationMs,
      status: response.status,
      ok: true,
    });
    return response;
  },
  (error) => {
    const startedAt = (error?.config as TimedAxiosConfig | undefined)?.__startedAt;
    const durationMs = startedAt ? Math.max(0, performance.now() - startedAt) : 0;
    recordApiSample({
      method: String(error?.config?.method || 'GET').toUpperCase(),
      path: String(error?.config?.url || ''),
      durationMs,
      status: Number(error?.response?.status || 0),
      ok: false,
    });

    const status = error?.response?.status;
    const code =
      error?.response?.data?.code ??
      error?.response?.data?.error?.code ??
      error?.response?.data?.originalError?.code;

    if (status === 403 && code === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new CustomEvent('petronexus:upgrade-required'));
      return Promise.reject(error);
    }

    if (status === 401) {
      // NEVER wipe tokens or redirect for auth endpoint 401s (login/register failures).
      // Only act on 401s from protected endpoints — those mean the JWT has expired or is invalid.
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/login') ||
                             requestUrl.includes('/auth/register') ||
                             requestUrl.includes('/auth/google');

      if (!isAuthEndpoint) {
        // Only redirect if not already on a public/marketing page and not already redirecting
        const publicPaths = [
          '/login', '/register', '/auth/callback', '/invite/accept',
          '/onboarding', '/forgot-password', '/reset-password',
          '/privacy', '/support',
        ];
        const isPublicPage =
          window.location.pathname === '/' ||
          publicPaths.some(p => window.location.pathname.startsWith(p));

        if (!isPublicPage) expireWebSession('session_expired');
      }
    }
    return Promise.reject(error);
  },
);

export default api;
